/**
 * @file Shared state and helpers for the measurement zoom lens: while magnifying a depthless
 * pano, they hide measurement graphics inside the lens circle so only the zoomed point cloud shows
 * there, discarding 3D lines/handlers in their shader and cutting HTML labels/toolbars out of a
 * container with a clip-path.
 */
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { ProjectionType } from "@/tools/multi-point-measures/multi-point-measures-controls-logic";
import { useThreeEventTarget } from "@faro-lotv/app-component-toolbox";
import type {
  BillboardScreenSpriteMaterial,
  LineMaterial,
} from "@faro-lotv/lotv";
import {
  createContext,
  PropsWithChildren,
  RefObject,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { IUniform, Vector3 } from "three";

/**
 * Shared state that hides measurement graphics inside the zoom lens circle while measuring on a
 * depthless pano, so only the magnified point cloud shows: 3D lines/handlers are discarded in their
 * shader, and HTML labels/toolbars are cut out of a shared container with a clip-path.
 */
export type ZoomLensClip = {
  /** `vec3(centerX, centerY, radius)` in framebuffer px (bottom-left origin); radius <= 0 disables the clip. */
  uniform: IUniform<Vector3>;

  /** Container the measurement HTML is portaled into so it can be clipped; null uses the default overlay. */
  labelContainerRef: RefObject<HTMLElement> | null;

  /**
   * Ref to a prop that reports whether the measuring hovered point
   * is being projected onto some axis or not. It influences the
   * behavior of the sticky target point inside the zoom lens.
   */
  projectionTypeRef: RefObject<ProjectionType>;
};

const ZoomLensClipContext = createContext<ZoomLensClip | undefined>(undefined);

/** @returns the zoom lens clip state of the enclosing scene, or a private inactive one if it has no lens */
export function useZoomLensClip(): ZoomLensClip {
  const clip = useContext(ZoomLensClipContext);
  // Private per consumer, so a scene without a provider can never write into another scene's clip.
  const [inactiveClip] = useState<ZoomLensClip>(() => ({
    uniform: { value: new Vector3() },
    labelContainerRef: null,
    projectionTypeRef: { current: ProjectionType.DoNotProject },
  }));
  return clip ?? inactiveClip;
}

/** @returns the shader uniform used to clip measurement materials out of the lens circle */
export function useZoomLensClipUniform(): IUniform<Vector3> {
  return useZoomLensClip().uniform;
}

/** @returns the element measurement HTML should be portaled into (the clipped lens container when active) */
export function useMeasurementLabelContainer(): RefObject<HTMLElement> {
  const overlay = useViewOverlayRef();
  return useZoomLensClip().labelContainerRef ?? overlay;
}

/** Added to the lens container while active; disables backdrop-filter so the clip can cut glassy children. */
export const ZOOM_LENS_CLIP_ACTIVE_CLASS = "zoom-lens-clip-active";

/** CSS that turns off `backdrop-filter` on clipped children so the clip-path can cut them out without being blurry. */
const ZOOM_LENS_CLIP_STYLE = `.${ZOOM_LENS_CLIP_ACTIVE_CLASS} * {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}`;

/**
 * Provides the zoom lens clip and mounts the container that hosts the clipped measurement HTML.
 *
 * @returns the provider that mounts the clipped measurement HTML container
 */
export function ZoomLensClipProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const overlay = useThreeEventTarget();
  const [uniform] = useState<IUniform<Vector3>>(() => ({
    value: new Vector3(),
  }));

  // Full-overlay, click-through `<div>` the measurement HTML (labels, toolbar) is portaled into as children,
  // so a single clip-path on this parent cuts the lens hole out of all of them at once.
  const [container] = useState(() => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.inset = "0";
    div.style.pointerEvents = "none";
    // Owned by the container so the rule dies with it
    const style = document.createElement("style");
    style.textContent = ZOOM_LENS_CLIP_STYLE;
    div.appendChild(style);
    return div;
  });
  const containerRef = useRef<HTMLElement>(container);

  const projectionTypeRef = useRef<ProjectionType>(ProjectionType.DoNotProject);

  useLayoutEffect(() => {
    overlay.appendChild(container);
    return () => container.remove();
  }, [overlay, container]);

  const value = useMemo<ZoomLensClip>(
    () => ({ uniform, labelContainerRef: containerRef, projectionTypeRef }),
    [uniform, containerRef, projectionTypeRef],
  );

  return <ZoomLensClipContext value={value}>{children}</ZoomLensClipContext>;
}

/** The lotv measurement materials that declare the screen-space clip uniform. */
type ScreenClippableMaterial = LineMaterial | BillboardScreenSpriteMaterial;

/**
 * Points a measurement material's screen-space clip uniform at the shared lens uniform, so the
 * material discards its own fragments inside the lens circle.
 *
 * @param material the measurement material (lotv line or handler sprite)
 * @param uniform the shared clip uniform, kept in sync by the rendering pipeline
 */
export function bindZoomLensClip(
  material: ScreenClippableMaterial,
  uniform: IUniform<Vector3>,
): void {
  // Shares the uniform object itself; the `screenClipCircle` setter would copy into the material's own one.
  material.uniforms.screenClipCircle = uniform;
}

/**
 * Moves the lens clip (shader uniform + HTML clip-path hole) to the given center and radius.
 *
 * @param clip the shared clip state
 * @param params the current lens geometry in overlay CSS pixels, plus the renderer DPR
 */
export function updateZoomLensClip(
  clip: ZoomLensClip,
  params: {
    center: { x: number; y: number };
    target: { x: number; y: number };
    radius: number;
    dpr: number;
    viewWidth: number;
    viewHeight: number;
  },
): void {
  const { center, target, radius, dpr, viewWidth, viewHeight } = params;

  // A non-positive radius means "no lens": keep the shader and HTML clip consistently disabled.
  if (radius <= 0) {
    clearZoomLensClip(clip);
    return;
  }

  // `gl_FragCoord` is bottom-left origin and DPR-scaled; the center arrives top-left in CSS px.
  clip.uniform.value.set(
    center.x * dpr,
    (viewHeight - center.y) * dpr,
    radius * dpr,
  );

  const el = clip.labelContainerRef?.current;
  if (el) {
    el.style.clipPath = lensClipPath(
      center,
      target,
      radius,
      viewWidth,
      viewHeight,
    );
    el.classList.add(ZOOM_LENS_CLIP_ACTIVE_CLASS);
  }
}

/**
 * Disables the lens clip for both the shader and the HTML container.
 *
 * @param clip the shared clip state
 */
export function clearZoomLensClip(clip: ZoomLensClip): void {
  clip.uniform.value.set(0, 0, 0);
  const el = clip.labelContainerRef?.current;
  if (el) {
    el.style.clipPath = "";
    el.classList.remove(ZOOM_LENS_CLIP_ACTIVE_CLASS);
  }
}

// The wedge leaves the circle 45° either side of the cursor direction, matching the pointer the lens
// artwork draws towards the cursor.
const POINTER_HALF_ANGLE = Math.PI / 4;

// A point on the lens circle at the given angle, as a rounded SVG "x y" pair.
function circlePoint(
  center: { x: number; y: number },
  radius: number,
  angle: number,
): string {
  const x = Math.round(center.x + radius * Math.cos(angle));
  const y = Math.round(center.y + radius * Math.sin(angle));
  return `${x} ${y}`;
}

// Clip-path that cuts a lens-shaped hole, the circle's 270° far arc closed with a wedge to the
// cursor, out of the full overlay, keeping the HTML (labels, toolbar) visible everywhere except
// inside the lens. Assumes the lens is offset from the cursor, so the cursor is the wedge's tip. The
// hole is one non-overlapping contour, so even-odd subtracts it cleanly from the outer rectangle.
function lensClipPath(
  center: { x: number; y: number },
  target: { x: number; y: number },
  radius: number,
  width: number,
  height: number,
): string {
  const r = Math.round(radius);
  const angle = Math.atan2(target.y - center.y, target.x - center.x);
  const box = `M0 0H${width}V${height}H0Z`;
  const arcStart = circlePoint(center, r, angle - POINTER_HALF_ANGLE);
  const arcEnd = circlePoint(center, r, angle + POINTER_HALF_ANGLE);
  const cursor = `${Math.round(target.x)} ${Math.round(target.y)}`;
  const lens = `M${arcStart}A${r} ${r} 0 1 0 ${arcEnd}L${cursor}Z`;
  return `path(evenodd, "${box} ${lens}")`;
}
